import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button, JournalEntryExternalLink } from '@cottage/design-system/internal-tooling/ui'
import WarningBanner from '@/components/WarningBanner'
import type { BackgroundJob } from '@/services/jobs'
import { isBackgroundJobActive } from '@/services/jobs'
import { qboJournalUrl } from '@/services/cloverBackfillAudit'
import {
  abortBackfillApplyRun,
  getBackfillApplyRun,
  listBackfillApplyRuns,
  postBackfillApplyExecute,
  postBackfillApplyPlan,
  type BackfillApplyPlan,
  type BackfillApplyPlanRow,
  type BackfillApplyRun,
} from '@/services/cloverBackfillApply'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import BackfillAuditLineComparison from './BackfillAuditLineComparison'
import BackfillApplySplitButton from './BackfillApplySplitButton'
import {
  applyButtonDisabledReason,
  applyButtonEnabled,
  applyCompletedBanner,
  applyRunErrorDetails,
  applyRunResultBadgeLabel,
  applyRunResultHumanStatus,
  applyRunResultTone,
  type ApplyRunResultTone,
  confirmModalReconLine,
  formatBackfillGiftCardDeltaMetric,
  formatBackfillSummaryMetricCount,
  isLeaveAloneBlocker,
  isLiveRunDetail,
  isNothingToApply,
  isPlanFresh,
  isReconDeletionBlocker,
  liveRunIsOpen,
  planActionCounts,
  planDecisionDrift,
  planEligibleRows,
  reconActionLabel,
  reconActionTone,
  runBelongsToMonth,
  showLiveReconJeDetails,
  runTieOutStores,
  tieOutPerStore,
  tieOutVarianceBefore,
} from './backfillApplyHelpers'
import { formatBackfillAccountingMoney, formatBackfillMoney } from './backfillAuditTableConfig'
import type { BackfillAuditListItem } from '@/services/cloverBackfillAudit'
import { cloverJobStatusLabel } from './cloverTokenSync'
import { useCloverJobSession } from './useCloverJobSession'

export type BackfillAuditSummaryTotals = {
  identical: number
  differs: number
  noActivity: number
  undecided: number
  giftCardDelta: number
}

type Props = {
  canEdit: boolean
  reportMonth: string | null
  summaryTotals: BackfillAuditSummaryTotals | null
  monthDiffers?: number
  auditRows?: BackfillAuditListItem[]
  showDryRunButton?: boolean
  onApplySucceeded?: () => void
}

const POLL_MS = 2000
const METRIC_VALUE_ROW_CLASS =
  'mt-0.5 flex h-6 items-center font-semibold tabular-nums text-gray-900'

function makeApplyPlaceholderJob(jobId: number, totalItems: number): BackgroundJob {
  return {
    id: jobId,
    job_type: 'clover_backfill_apply',
    status: 'pending',
    total_items: totalItems,
    processed_items: 0,
    successful_items: 0,
    failed_items: 0,
    created_at: new Date().toISOString(),
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function planRowKey(row: BackfillApplyPlanRow): string {
  return `${row.store}|${row.date}`
}

function pathLabel(path: BackfillApplyPlanRow['path']): string {
  return path === 'update_existing_draft' ? 'update' : 'adopt'
}

function ApplyConfirmModal({
  open,
  reportMonth,
  plan,
  onClose,
  onConfirm,
}: {
  open: boolean
  reportMonth: string
  plan: BackfillApplyPlan
  onClose: () => void
  onConfirm: () => void
}) {
  if (!open) return null
  const counts = planActionCounts(plan)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-confirm-title"
        className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="apply-confirm-title" className="text-sm font-semibold text-gray-900">
          Apply to QBO for {reportMonth}?
        </h3>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
          <li>{counts.update} row(s) to update</li>
          <li>{counts.adopt} row(s) to adopt</li>
          <li>{counts.skip} row(s) to skip</li>
          <li>{confirmModalReconLine(plan)}</li>
        </ul>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            gradient
            size="md"
            onClick={() => {
              onClose()
              onConfirm()
            }}
          >
            Apply to QBO
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function CloverBackfillApplyCard({
  canEdit,
  reportMonth,
  summaryTotals,
  monthDiffers,
  auditRows = [],
  showDryRunButton = false,
  onApplySucceeded,
}: Props) {
  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const removeServerJob = useBackgroundJobsStore((s) => s.removeServerJob)
  const serverById = useBackgroundJobsStore((s) => s.serverById)

  const [dryRun, setDryRun] = useState<BackfillApplyRun | null>(null)
  const [liveRun, setLiveRun] = useState<BackfillApplyRun | null>(null)
  const [planJobId, setPlanJobId] = useState<number | null>(null)
  const [liveJobId, setLiveJobId] = useState<number | null>(null)
  const [dryRunLoading, setDryRunLoading] = useState(false)
  const [executeLoading, setExecuteLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [expandedPlanRow, setExpandedPlanRow] = useState<string | null>(null)
  const reportMonthRef = useRef(reportMonth)
  reportMonthRef.current = reportMonth

  const metrics = summaryTotals ?? {
    identical: 0,
    differs: 0,
    noActivity: 0,
    undecided: 0,
    giftCardDelta: Number.NaN,
  }

  const activeApplyJob = useMemo(() => {
    for (const { job } of Object.values(serverById)) {
      if (job.job_type === 'clover_backfill_apply' && isBackgroundJobActive(job.status)) {
        return job
      }
    }
    return null
  }, [serverById])

  const trackedJob =
    (liveJobId ? serverById[liveJobId]?.job : null) ??
    (planJobId ? serverById[planJobId]?.job : null) ??
    activeApplyJob

  useCloverJobSession(trackedJob?.status ?? null)

  const loadRunsHistory = useCallback(async (month: string) => {
    const stillSelected = () => month === reportMonthRef.current
    try {
      const runs = await listBackfillApplyRuns(month)
      if (!stillSelected()) return
      const sorted = [...runs].sort(
        (left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
      )
      const appliedDryRunIds = new Set(
        sorted
          .filter((run) => run.mode === 'live' && run.parent_run_id != null)
          .map((run) => run.parent_run_id as number),
      )
      const latestDry = sorted.find(
        (run) =>
          run.mode === 'dry_run' &&
          run.status === 'completed' &&
          !appliedDryRunIds.has(run.id),
      )
      if (latestDry) {
        try {
          const dryDetail = await getBackfillApplyRun(latestDry.id)
          if (!stillSelected()) return
          setDryRun(dryDetail.plan ? dryDetail : null)
        } catch {
          if (!stillSelected()) return
          setDryRun(null)
        }
      } else {
        setDryRun(null)
      }
      const latestLive = sorted.find((run) => run.mode === 'live')
      if (latestLive) {
        try {
          const liveDetail = await getBackfillApplyRun(latestLive.id)
          if (!stillSelected()) return
          setLiveRun((current) => {
            if (
              current?.id === liveDetail.id &&
              isLiveRunDetail(current) &&
              !isLiveRunDetail(liveDetail)
            ) {
              return current
            }
            return liveDetail
          })
        } catch {
          if (!stillSelected()) return
          setLiveRun((current) => {
            if (current?.id === latestLive.id && isLiveRunDetail(current)) return current
            return latestLive
          })
        }
      } else {
        setLiveRun((current) => (runBelongsToMonth(current, month) ? current : null))
      }
    } catch (error) {
      if (!stillSelected()) return
      toast.error(formatApiError(error, 'Failed to load apply runs'))
    }
  }, [])

  useEffect(() => {
    if (!reportMonth) {
      setDryRun(null)
      setLiveRun(null)
      return
    }
    setLiveRun((current) => (runBelongsToMonth(current, reportMonth) ? current : null))
    setDryRun((current) => (runBelongsToMonth(current, reportMonth) ? current : null))
    setConfirmOpen(false)
    void loadRunsHistory(reportMonth)
  }, [loadRunsHistory, reportMonth])

  const pollRunUntilReady = useCallback(
    async (runId: number, jobId?: number) => {
      if (jobId) {
        setPlanJobId(jobId)
        registerServerJob(makeApplyPlaceholderJob(jobId, 62))
      }
      for (let attempt = 0; attempt < 120; attempt += 1) {
        const run = await getBackfillApplyRun(runId)
        if (run.plan || run.status === 'completed' || run.status === 'completed_with_errors') {
          return run
        }
        if (run.status === 'failed' || run.status === 'aborted') {
          throw new Error(`Dry run ${run.status}`)
        }
        if (run.job_id && !jobId) {
          setPlanJobId(run.job_id)
          registerServerJob(makeApplyPlaceholderJob(run.job_id, 62))
        }
        await sleep(POLL_MS)
      }
      throw new Error('Timed out waiting for dry-run plan')
    },
    [registerServerJob],
  )

  const pollLiveRun = useCallback(
    async (runId: number, jobId: number) => {
      setLiveJobId(jobId)
      registerServerJob(makeApplyPlaceholderJob(jobId, dryRun?.plan?.rows.length ?? 1))
      for (let attempt = 0; attempt < 600; attempt += 1) {
        const run = await getBackfillApplyRun(runId)
        if (runBelongsToMonth(run, reportMonthRef.current)) {
          setLiveRun(run)
        }
        if (
          run.status === 'completed' ||
          run.status === 'completed_with_errors' ||
          run.status === 'failed' ||
          run.status === 'aborted'
        ) {
          removeServerJob(jobId)
          return run
        }
        await sleep(POLL_MS)
      }
      throw new Error('Timed out waiting for apply run')
    },
    [dryRun?.plan?.rows.length, registerServerJob, removeServerJob],
  )

  useEffect(() => {
    const jobId = liveRun?.job_id ?? liveJobId ?? planJobId
    if (!jobId || !liveRun) return
    if (
      liveRun.mode === 'live' &&
      (liveRun.status === 'completed' ||
        liveRun.status === 'completed_with_errors' ||
        liveRun.status === 'failed' ||
        liveRun.status === 'aborted')
    ) {
      removeServerJob(jobId)
    }
  }, [liveJobId, liveRun, planJobId, removeServerJob])

  const handleDryRun = useCallback(async (force = false) => {
    if (!canEdit || !reportMonth) return
    if (!force && dryRun?.report_month === reportMonth && dryRun.plan && isPlanFresh(dryRun)) {
      toast.success('Using cached dry run')
      return
    }
    setDryRunLoading(true)
    setLiveRun(null)
    setLiveJobId(null)
    setConfirmOpen(false)
    if (force) {
      setDryRun(null)
      setExpandedPlanRow(null)
    }
    try {
      const response = await postBackfillApplyPlan({
        report_month: reportMonth,
        verify_rebuild: true,
        force,
      })
      let run: BackfillApplyRun
      if (response.plan) {
        run = await getBackfillApplyRun(response.run_id)
      } else {
        run = await pollRunUntilReady(response.run_id, response.job_id)
      }
      setDryRun(run)
      toast.success('Dry run complete')
      void loadRunsHistory(reportMonth)
    } catch (error) {
      toast.error(formatApiError(error, 'Dry run failed'))
    } finally {
      setDryRunLoading(false)
      setPlanJobId(null)
    }
  }, [canEdit, dryRun, loadRunsHistory, pollRunUntilReady, reportMonth])

  const handleExecute = useCallback(async () => {
    if (!canEdit || !dryRun?.plan || !reportMonth) return
    setExecuteLoading(true)
    try {
      const response = await postBackfillApplyExecute(dryRun.id, {
        confirmation: reportMonth,
      })
      const run = await pollLiveRun(response.run_id, response.job_id)
      if (runBelongsToMonth(run, reportMonthRef.current)) {
        setLiveRun(run)
      }
      setConfirmOpen(false)
      setDryRun(null)
      if (run.mode === 'live' && run.status === 'completed') {
        onApplySucceeded?.()
      }
      toast.success('Apply job finished')
      void loadRunsHistory(reportMonth)
    } catch (error) {
      toast.error(formatApiError(error, 'Apply failed'))
    } finally {
      setExecuteLoading(false)
      setLiveJobId(null)
    }
  }, [canEdit, dryRun, loadRunsHistory, onApplySucceeded, pollLiveRun, reportMonth])

  const handleAbort = useCallback(async () => {
    const runId = liveRun?.id ?? dryRun?.id
    if (!canEdit || !runId) return
    try {
      await abortBackfillApplyRun(runId)
      toast.success('Abort requested')
      if (reportMonth) void loadRunsHistory(reportMonth)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to abort run'))
    }
  }, [canEdit, dryRun?.id, liveRun?.id, loadRunsHistory, reportMonth])

  const plan = runBelongsToMonth(dryRun, reportMonth) ? dryRun?.plan ?? null : null
  const applyEnabled =
    canEdit && applyButtonEnabled({ dryRun, selectedMonth: reportMonth, auditRows })
  const applyDisabledReason = applyButtonDisabledReason({
    dryRun,
    selectedMonth: reportMonth,
    auditRows,
  })
  const liveOpen = liveRunIsOpen(liveRun)
  const applyDisabled = !applyEnabled || Boolean(trackedJob) || executeLoading || liveOpen
  const dryRunDisabled = !canEdit || !reportMonth || dryRunLoading || Boolean(trackedJob)

  const resultRun =
    liveRun?.mode === 'live' && runBelongsToMonth(liveRun, reportMonth) ? liveRun : null

  const nothingToApply = isNothingToApply({
    plan,
    differsCount: monthDiffers ?? metrics.differs,
  })
  const planHasNoEligible = plan != null && planEligibleRows(plan).length === 0
  const planHasNoRows = plan != null && plan.rows.length === 0
  const decisionDrift = useMemo(
    () => (plan ? planDecisionDrift(plan, auditRows) : []),
    [auditRows, plan],
  )
  const reconAction = plan?.recon_action ?? (plan?.recon_deletable ? 'delete' : 'none_needed')
  const reconTone = reconActionTone(reconAction)
  const showReconDetails = plan ? showLiveReconJeDetails(plan) : false

  const reconDeletionBlockers = useMemo(
    () => (plan?.blockers ?? []).filter((blocker) => isReconDeletionBlocker(blocker.reason)),
    [plan],
  )

  const leaveAloneBlockers = useMemo(
    () => (plan?.blockers ?? []).filter((blocker) => isLeaveAloneBlocker(blocker.reason)),
    [plan],
  )

  const blockingBlockers = useMemo(
    () => (plan?.blockers ?? []).filter((blocker) => !isLeaveAloneBlocker(blocker.reason)),
    [plan],
  )

  const runTieOut = resultRun ? runTieOutStores(resultRun) : null
  const completedBanner = applyCompletedBanner(resultRun)
  const runErrorDetails = resultRun ? applyRunErrorDetails(resultRun) : null
  const showErrorResultPanel = Boolean(
    resultRun &&
      (resultRun.status === 'completed_with_errors' ||
        resultRun.status === 'failed' ||
        resultRun.status === 'aborted'),
  )
  const hasStructuredErrorDetails = Boolean(
    runErrorDetails &&
      (runErrorDetails.failedRows.length > 0 ||
        runErrorDetails.residuals.length > 0 ||
        runErrorDetails.reconNote ||
        runErrorDetails.tieOutError ||
        runErrorDetails.resultError ||
        runErrorDetails.applied != null ||
        runErrorDetails.failed != null ||
        runErrorDetails.skipped != null ||
        Object.keys(runErrorDetails.expected).length > 0 ||
        Object.keys(runErrorDetails.actual).length > 0),
  )

  const runResultTone: ApplyRunResultTone = resultRun ? applyRunResultTone(resultRun) : 'neutral'
  const runResultBadgeLabel = resultRun
    ? applyRunResultBadgeLabel(runResultTone, resultRun.status)
    : null
  const runResultToneStyles: Record<
    ApplyRunResultTone,
    { border: string; header: string; title: string; badge: string }
  > = {
    success: {
      border: 'border-emerald-200',
      header: 'border-emerald-200 bg-emerald-50/70',
      title: 'text-emerald-950',
      badge: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
    },
    warning: {
      border: 'border-amber-200',
      header: 'border-amber-200 bg-amber-50',
      title: 'text-amber-950',
      badge: 'bg-amber-50 text-amber-950 ring-1 ring-amber-300',
    },
    danger: {
      border: 'border-red-200',
      header: 'border-red-200 bg-red-50',
      title: 'text-red-950',
      badge: 'bg-red-100 text-red-800 ring-1 ring-red-200',
    },
    neutral: {
      border: 'border-gray-200',
      header: 'border-gray-200 bg-gray-50',
      title: 'text-gray-900',
      badge: '',
    },
  }
  const runResultStyles = runResultToneStyles[runResultTone]

  return (
    <>
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
        <div className="flex flex-wrap items-end gap-4 text-sm">
          <div className="w-[150px] shrink-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Identical</p>
            <p className={METRIC_VALUE_ROW_CLASS}>{formatBackfillSummaryMetricCount(metrics.identical)}</p>
          </div>
          <div className="w-[150px] shrink-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Differs</p>
            <p className={METRIC_VALUE_ROW_CLASS}>{formatBackfillSummaryMetricCount(metrics.differs)}</p>
          </div>
          <div className="w-[150px] shrink-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Undecided</p>
            <p className={METRIC_VALUE_ROW_CLASS}>{formatBackfillSummaryMetricCount(metrics.undecided)}</p>
          </div>
          <div className="w-[150px] shrink-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">No activity</p>
            <p className="mt-0.5 flex h-6 items-center font-semibold tabular-nums text-gray-500">
              {formatBackfillSummaryMetricCount(metrics.noActivity)}
            </p>
          </div>
          <div className="w-[150px] shrink-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Σ gift-card delta
            </p>
            <p className={METRIC_VALUE_ROW_CLASS}>
              {formatBackfillGiftCardDeltaMetric(metrics.giftCardDelta)}
            </p>
          </div>
          <div className="ml-auto shrink-0">
            {showDryRunButton && (!plan || planHasNoRows) ? (
              <Button
                variant="primary"
                gradient
                size="md"
                disabled={dryRunDisabled}
                onClick={() => void handleDryRun(Boolean(planHasNoRows))}
              >
                {dryRunLoading
                  ? planHasNoRows
                    ? 'Regenerating…'
                    : 'Dry running…'
                  : planHasNoRows
                    ? 'Regenerate'
                    : 'Dry run'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {trackedJob ? (
          <p className="text-sm text-gray-700">
            Job {trackedJob.id}: {cloverJobStatusLabel(trackedJob.status)}
            {trackedJob.total_items > 0
              ? ` · ${trackedJob.processed_items}/${trackedJob.total_items}`
              : ''}
            {canEdit ? (
              <Button variant="outline" size="sm" className="ml-2" onClick={() => void handleAbort()}>
                Abort
              </Button>
            ) : null}
          </p>
        ) : null}

        {plan?.verify_rebuild_warning ? (
          <WarningBanner message="Rebuild verification reported warnings. Review the plan before applying." />
        ) : null}

        {decisionDrift.length > 0 ? (
          <WarningBanner
            message={`This dry run is out of date — ${decisionDrift.length} row(s) changed decision since it was generated. Run dry run again.`}
          />
        ) : null}

        {nothingToApply || planHasNoEligible ? (
          <p className="text-sm text-gray-500">Nothing to apply for this month.</p>
        ) : null}

        {plan ? (
          <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Dry-run plan</h3>

            {blockingBlockers.length > 0 ? (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Blockers</h4>
                <ul className="mt-2 space-y-1 text-sm text-red-800">
                  {blockingBlockers.map((blocker) => (
                    <li key={`${blocker.store}-${blocker.date}-${blocker.reason}`}>
                      {blocker.date} · {blocker.store} · {blocker.reason}
                      {blocker.detail ? ` — ${blocker.detail}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {leaveAloneBlockers.length > 0 ? (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Left alone
                </h4>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  {leaveAloneBlockers.map((blocker) => (
                    <li key={`${blocker.store}-${blocker.date}-${blocker.reason}`}>
                      {blocker.date} · {blocker.store} · {blocker.reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="table-forecast w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="w-8" />
                    <th>Date</th>
                    <th>Store</th>
                    <th>Path</th>
                    <th>Diff summary</th>
                    <th className="text-right">Δ debits</th>
                    <th>Stale</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.rows.map((row) => {
                    const key = planRowKey(row)
                    const expanded = expandedPlanRow === key
                    return (
                      <Fragment key={key}>
                        <tr key={key} className="group">
                          <td>
                            <button
                              type="button"
                              className="rounded p-1 text-gray-500 hover:bg-gray-200"
                              aria-label={expanded ? 'Collapse row' : 'Expand row'}
                              onClick={() => setExpandedPlanRow(expanded ? null : key)}
                            >
                              {expanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td>{row.date}</td>
                          <td>{row.store}</td>
                          <td>{pathLabel(row.path)}</td>
                          <td className="max-w-xs truncate" title={row.diff_summary ?? ''}>
                            {row.diff_summary ?? '—'}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatBackfillAccountingMoney(row.delta_debits)}
                          </td>
                          <td>{row.stale ?? '—'}</td>
                        </tr>
                        {expanded ? (
                          <tr key={`${key}-detail`} className="bg-white">
                            <td colSpan={7} className="px-4 py-3">
                              <BackfillAuditLineComparison
                                beforeLines={row.before ?? []}
                                afterLines={row.after ?? []}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                  {plan.rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-sm text-gray-500">
                        No rows to apply.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {!planHasNoRows ? (
              <>
            <div className="text-sm text-gray-700">
              {showReconDetails ? (
                <>
                  <div className="font-medium text-gray-900">
                    Recon plug
                    {plan.recon_je?.doc_number ? ` · ${plan.recon_je.doc_number}` : ''}
                    {plan.recon_je?.qbo_id ? (
                      <JournalEntryExternalLink
                        href={qboJournalUrl(plan.recon_je.qbo_id)}
                        className="ml-2"
                      >
                        Open in QBO
                      </JournalEntryExternalLink>
                    ) : null}
                  </div>
                  {plan.recon_je?.amount_by_store &&
                  Object.keys(plan.recon_je.amount_by_store).length > 0 ? (
                    <ul className="mt-1 list-disc pl-5 text-xs">
                      {Object.entries(plan.recon_je.amount_by_store).map(([store, amount]) => (
                        <li key={store}>
                          {store}: {formatBackfillMoney(amount)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : plan.recon_je?.doc_number ? (
                <p className="text-xs text-gray-500">
                  Former draft: {plan.recon_je.doc_number}
                </p>
              ) : null}
              <p
                className={
                  reconTone === 'muted'
                    ? 'mt-2 text-xs text-gray-500'
                    : reconTone === 'amber'
                      ? 'mt-2 text-xs text-amber-800'
                      : 'mt-2 text-xs text-gray-600'
                }
              >
                {reconAction === 'blocked'
                  ? `${reconActionLabel(reconAction)}${
                      plan.recon_not_deletable_reason
                        ? ` because ${plan.recon_not_deletable_reason}`
                        : reconDeletionBlockers.length > 0
                          ? ' because open rows remain in this month'
                          : ''
                    }.`
                  : reconActionLabel(reconAction)}
              </p>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Expected tie-out after apply
                </h4>
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                  {Object.entries(tieOutPerStore(plan.tie_out_expected_after)).map(([store, value]) => {
                    const before = tieOutVarianceBefore(plan.tie_out_expected_after)[store]
                    return (
                      <li key={store}>
                        {store}:{' '}
                        {before !== undefined ? (
                          <>
                            {formatBackfillAccountingMoney(before)} →{' '}
                          </>
                        ) : null}
                        {formatBackfillAccountingMoney(value)}
                      </li>
                    )
                  })}
                </ul>
                {plan.tie_out_expected_after.cumulative_note ? (
                  <p className="mt-2 text-xs text-gray-500">
                    {plan.tie_out_expected_after.cumulative_note}
                  </p>
                ) : null}
              </div>
              {canEdit ? (
                <BackfillApplySplitButton
                  primaryLabel={executeLoading ? 'Applying…' : 'Apply to QBO'}
                  applyDisabled={applyDisabled}
                  applyDisabledTitle={applyDisabled ? applyDisabledReason ?? undefined : undefined}
                  menuDisabled={dryRunDisabled}
                  onApply={() => setConfirmOpen(true)}
                  onRegenerate={() => void handleDryRun(true)}
                />
              ) : null}
            </div>
              </>
            ) : null}
          </div>
        ) : null}

        {resultRun && resultRun.mode === 'live' ? (
          <div className={`overflow-hidden rounded-lg border ${runResultStyles.border}`}>
            <div
              className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${runResultStyles.header}`}
            >
              <h3 className={`text-sm font-semibold ${runResultStyles.title}`}>
                Run result · {resultRun.report_month} ·{' '}
                {applyRunResultHumanStatus(resultRun.status)}
              </h3>
              {runResultBadgeLabel ? (
                <span
                  className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-semibold ${runResultStyles.badge}`}
                >
                  {runResultTone === 'success' ? (
                    <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : null}
                  {runResultBadgeLabel}
                </span>
              ) : null}
            </div>
            <div className="space-y-3 p-4">
            {liveOpen && !resultRun.result ? (
              <p className="text-sm text-gray-600">
                This live apply is still marked running but has no recorded result. If the job
                already stopped (for example after a save error), abort it, then dry-run again.
                Check whether those days already show as applied before applying.
              </p>
            ) : null}
            {liveOpen && canEdit ? (
              <Button variant="outline" size="sm" onClick={() => void handleAbort()}>
                Abort
              </Button>
            ) : null}
            {completedBanner ? <WarningBanner message={completedBanner.message} /> : null}
            {showErrorResultPanel ? (
              <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm text-gray-700">
                {runErrorDetails &&
                (runErrorDetails.applied != null ||
                  runErrorDetails.failed != null ||
                  runErrorDetails.skipped != null) ? (
                  <p>
                    Applied {runErrorDetails.applied ?? 0} · Failed {runErrorDetails.failed ?? 0} ·
                    Skipped {runErrorDetails.skipped ?? 0}
                  </p>
                ) : null}
                {!runErrorDetails?.hasResult ? (
                  <p className="text-amber-900">
                    Could not load run result. Status is {resultRun.status}.
                  </p>
                ) : null}
                {runErrorDetails?.resultError ? (
                  <p className="text-red-800">{runErrorDetails.resultError}</p>
                ) : null}
                {runErrorDetails?.tieOutError ? (
                  <p className="text-red-800">Tie-out error: {runErrorDetails.tieOutError}</p>
                ) : null}
                {runErrorDetails && runErrorDetails.failedRows.length > 0 ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Failed days
                    </div>
                    <ul className="mt-2 space-y-1 text-red-800">
                      {runErrorDetails.failedRows.map((row) => (
                        <li key={`${row.store}-${row.date}`}>
                          {row.store} · {row.date} · {row.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {runErrorDetails &&
                (Object.keys(runErrorDetails.expected).length > 0 ||
                  Object.keys(runErrorDetails.actual).length > 0) ? (
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Per-store tie-out
                      </span>
                      {runTieOut?.monthTies ? (
                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
                          ties
                        </span>
                      ) : null}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {Object.keys({
                        ...runErrorDetails.expected,
                        ...runErrorDetails.actual,
                      }).map((store) => (
                        <li key={store}>
                          {store}: expected{' '}
                          {formatBackfillAccountingMoney(runErrorDetails.expected[store] ?? '0')} ·
                          actual{' '}
                          {formatBackfillAccountingMoney(runErrorDetails.actual[store] ?? '0')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {runErrorDetails && runErrorDetails.residuals.length > 0 ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Tie-out residuals
                    </div>
                    <p className="mt-2 text-amber-800">
                      Gift-card tie-out did not match expected after apply for:{' '}
                      {runErrorDetails.residuals.join(', ')}. This is a variance check, not a
                      missing recon plug.
                    </p>
                  </div>
                ) : null}
                {runErrorDetails?.reconNote &&
                (runErrorDetails.reconNote.includes('not deleted') ||
                  runErrorDetails.reconNote.includes('blocked')) ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Recon plug
                    </div>
                    <p className="mt-2 text-gray-700">{runErrorDetails.reconNote}</p>
                  </div>
                ) : null}
              </div>
            ) : runTieOut && Object.keys(runTieOut.expected).length > 0 ? (
              <div className="text-sm text-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Tie-out
                  </span>
                  {runTieOut.monthTies ? (
                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
                      ties
                    </span>
                  ) : null}
                </div>
                <ul className="mt-2 space-y-1">
                  {Object.keys(runTieOut.expected).map((store) => (
                    <li key={store}>
                      {store}: expected {formatBackfillAccountingMoney(runTieOut.expected[store])} ·
                      actual {formatBackfillAccountingMoney(runTieOut.actual[store] ?? '0')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {resultRun.result ? (
              hasStructuredErrorDetails ? (
                <details className="text-sm text-gray-700">
                  <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Raw result JSON
                  </summary>
                  <pre className="mt-1 overflow-x-auto text-xs">
                    {JSON.stringify(resultRun.result, null, 2)}
                  </pre>
                </details>
              ) : (
                <div className="text-sm text-gray-700">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Raw result JSON
                  </div>
                  <pre className="mt-1 overflow-x-auto text-xs">
                    {JSON.stringify(resultRun.result, null, 2)}
                  </pre>
                </div>
              )
            ) : null}
            {resultRun.recon_je?.qbo_id &&
            !resultRun.recon_je.deleted_at &&
            resultRun.result?.recon?.action === 'delete' &&
            !resultRun.result.recon.deleted ? (
              <p className="text-sm text-amber-800">
                Recon JE was not deleted.{' '}
                <JournalEntryExternalLink href={qboJournalUrl(resultRun.recon_je.qbo_id)}>
                  {resultRun.recon_je.doc_number}
                </JournalEntryExternalLink>
              </p>
            ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {plan && reportMonth ? (
        <ApplyConfirmModal
          open={confirmOpen}
          reportMonth={reportMonth}
          plan={plan}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => void handleExecute()}
        />
      ) : null}
    </>
  )
}
